import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteAlbumVideoCommentResult,
  DeleteAlbumVideoCommentOptions,
  PatchAlbumVideoCommentResult,
  PatchAlbumVideoCommentOptions,
  DeleteAlbumVideoCommentFields,
  PatchAlbumVideoCommentFields,
  deleteAlbumVideoComment,
  patchAlbumVideoComment,
} from '../fetchers/album/video/comment';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseDeleteAlbumVideoCommentOptions<Fields extends DeleteAlbumVideoCommentFields> =
  DeleteAlbumVideoCommentOptions<Fields>;

/**
 * Delete a video comment
 * This method deletes the specified video comment. The authenticated user must be the owner of the comment.
 */

export function useDeleteAlbumVideoComment<Fields extends DeleteAlbumVideoCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteAlbumVideoCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteAlbumVideoCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}/comments/${
            options.where.commentId
          }${serializeQuery(options)}`,
          deleteAlbumVideoComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteAlbumVideoComment, {
    endpoint: '/albums/:albumId/videos/:videoId/comments/:commentId',
    method: 'DELETE',
  });
}

type UsePatchAlbumVideoCommentOptions<Fields extends PatchAlbumVideoCommentFields> =
  PatchAlbumVideoCommentOptions<Fields>;

/**
 * Edit a video comment
 * This method edits the specified video comment. The authenticated user must be the owner of the comment.
 */

export function usePatchAlbumVideoComment<Fields extends PatchAlbumVideoCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchAlbumVideoCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchAlbumVideoCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}/comments/${
            options.where.commentId
          }${serializeQuery(options)}`,
          patchAlbumVideoComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchAlbumVideoComment, {
    endpoint: '/albums/:albumId/videos/:videoId/comments/:commentId',
    method: 'PATCH',
  });
}
